import { useState } from "react";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { StepHeader } from "./StepHeader";
import { Summary } from "./Summary";

const Field = ({ label, children }) => (
  <label className="block"><span className="block text-sm text-white/80 mb-2">{label}</span>{children}</label>
);
const inputCls = "bg-black/50 border-white/15 h-11 focus-visible:ring-primary";

const AddressFields = ({ f, title }) => (
  <div data-testid="address-fields" className="space-y-4">
    <div className="flex items-center gap-2 text-sm text-white/80"><MapPin size={16} className="text-primary" /> {title}</div>
    <div className="grid grid-cols-[1fr_110px] gap-3">
      <Input data-testid="input-street" required placeholder="Ulica" className={inputCls} {...f("street")} />
      <Input data-testid="input-house-no" required placeholder="Nr domu / lok." className={inputCls} {...f("house_no")} />
    </div>
    <div className="grid grid-cols-[120px_1fr] gap-3">
      <Input data-testid="input-postal-code" required pattern="\d{2}-\d{3}" title="Format 00-000" placeholder="00-000" className={inputCls} {...f("postal_code")} />
      <Input data-testid="input-city" required placeholder="Miasto" className={inputCls} {...f("city")} />
    </div>
  </div>
);

export const StepDetails = ({ ctx }) => {
  const { b, update, go, setDone } = ctx;
  const [busy, setBusy] = useState(false);
  const mobile = b.service === "mobile";
  const needsAddress = mobile || !!b.pickup;
  const f = (k) => ({ value: b[k] || "", onChange: (e) => update({ [k]: e.target.value }) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        service_type: b.service, car_type: b.car, package_id: b.packageId, addon_ids: b.addons,
        date: b.date, time: b.time, name: b.name, phone: b.phone, email: b.email,
        invoice: !!b.invoice, nip: b.nip || "", notes: b.notes || "", pickup: !mobile && !!b.pickup,
        street: b.street || "", house_no: b.house_no || "", postal_code: b.postal_code || "", city: b.city || "",
      });
      setDone(data);
    } catch (err) {
      toast.error(errMsg(err));
      if (err?.response?.status === 409) { update({ time: null }); go("term"); }
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="step-details">
      <StepHeader onBack={() => go("term")} title="Twoje dane" sub={`Podaj swoje dane kontaktowe${needsAddress ? " oraz adres" : ""}.`} />
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-5 rise d1">
          <Field label="Imię i nazwisko"><Input data-testid="input-name" required minLength={2} placeholder="Jan Kowalski" className={inputCls} {...f("name")} /></Field>
          <Field label="Numer telefonu"><Input data-testid="input-phone" required minLength={6} type="tel" placeholder="+48 123 456 789" className={inputCls} {...f("phone")} /></Field>
          <Field label="E-mail"><Input data-testid="input-email" required type="email" placeholder="jan@przyklad.pl" className={inputCls} {...f("email")} /></Field>
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <Checkbox data-testid="input-invoice" checked={!!b.invoice} onCheckedChange={(v) => update({ invoice: !!v })} className="data-[state=checked]:bg-primary data-[state=checked]:text-black border-white/40" />
            Chcę otrzymać fakturę (opcjonalnie)
          </label>
          {b.invoice && <Field label="NIP"><Input data-testid="input-nip" placeholder="1234567890" className={inputCls} {...f("nip")} /></Field>}
          {!mobile && (
            <div className="rounded-lg border border-white/15 bg-black/40 p-4">
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <Checkbox data-testid="input-pickup" checked={!!b.pickup} onCheckedChange={(v) => update({ pickup: !!v })} className="data-[state=checked]:bg-primary data-[state=checked]:text-black border-white/40" />
                Chcę skorzystać z odbioru auta spod mojego adresu
              </label>
              <p data-testid="pickup-fee-note" className="text-xs text-primary/90 mt-2 ml-7">* Za odbiór auta zostanie naliczona dodatkowa opłata.</p>
            </div>
          )}
          {needsAddress && <AddressFields f={f} title={mobile ? "Adres wykonania usługi" : "Adres odbioru auta"} />}
          <Field label="Uwagi (opcjonalnie)"><Textarea data-testid="input-notes" rows={3} className="bg-black/50 border-white/15" {...f("notes")} /></Field>
        </div>
        <div className="rise d2">
          <Summary ctx={ctx} title="Wybrana usługa" showTerm>
            <button data-testid="submit-booking-button" type="submit" disabled={busy} className="btn-y w-full py-3.5 mt-6 text-sm">
              {busy ? "Rezerwuję..." : "Rezerwuję termin"}
            </button>
          </Summary>
        </div>
      </form>
    </section>
  );
};
